import { useApiQuery } from '@/hooks/useApiQuery'
import type { ApiRequestError } from '@/services/api'
import { robotsApi, type ListAlertsResponse, type RobotMediaItem } from '@/services/api/domains/robots.api'
import { overlayDefined } from '@/services/api/robot-alerts-merge'
import type { RobotAlertItem } from '@/services/api/robot-alerts-store'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDashboardBridgeOptional } from '../Dashboard/useDashboardBridge'
import { beginMediaPrime, primeMediaUrls } from '../RobotMedia/robot-media-ref'
import { SERVER_LIST_CAP, type AlertLogScope } from './alert-log'

/**
 * The data behind the alert log: everything the live store does not carry.
 *
 * The store holds OPEN alerts and is fast. The log also needs handled alerts (only a fetch has
 * them) and, for a selected helmet, its pictures (nothing pushes those at all). This hook owns
 * those two fetches and, above all, WHEN they run — because the wrong cadence here is either a
 * second poll on top of the store's, or a picture that takes five minutes to appear.
 */

/** A store change refetches at most this often; a burst of live frames ends with one fetch. */
export const LOG_REFETCH_THROTTLE_MS = 1_500
/** Pictures re-check while a helmet is selected on a dashboard. Never in fleet scope. */
export const MEDIA_REFETCH_MS = 30_000
/** After an alarm or a Take-photo click: an upload takes seconds, the tick is minutes away. */
export const MEDIA_FOLLOWUP_MS: readonly number[] = [3_000, 8_000, 15_000]

export interface AlertLogFeed {
  /** All-status alerts for the current scope; empty until the scope's own fetch has answered. */
  fetched: RobotAlertItem[]
  /** The selected helmet's media rows; always empty in fleet scope. */
  media: RobotMediaItem[]
  /** Alerts the store held at some point this mount — the flicker guard, see `mergeAlertLog`. */
  seen: RobotAlertItem[]
  error: ApiRequestError | null
  /** A fetch that came back exactly at the server cap is almost certainly truncated. */
  capped: { alerts: boolean; pictures: boolean }
  refetch: () => Promise<void>
}

const NO_ALERTS: RobotAlertItem[] = []
const NO_MEDIA: RobotMediaItem[] = []

function scopeKeyOf(scope: AlertLogScope): string {
  return scope.kind === 'robot' ? scope.robotId : 'fleet'
}

export function useAlertLogFeed(
  scope: AlertLogScope,
  storeAlerts: readonly RobotAlertItem[],
  elementId?: string,
): AlertLogFeed {
  const bridge = useDashboardBridgeOptional()
  const scopeKey = scopeKeyOf(scope)
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const robotId = scope.kind === 'robot' ? scope.robotId : undefined

  // Results are STAMPED with the scope they were requested for, inside their own promise chain,
  // and accepted only if that scope is still current when they land — so a scope change clears
  // the list synchronously, and a response that outlived its scope is never shown. Each chain
  // carries its own key: a shared ref would let an older in-flight request relabel a newer one's
  // result when the two resolve back to back.
  const [alertsStamp, setAlertsStamp] = useState<{ key: string; rows: RobotAlertItem[] }>({ key: '', rows: NO_ALERTS })
  const [mediaStamp, setMediaStamp] = useState<{ key: string; rows: RobotMediaItem[] }>({ key: '', rows: NO_MEDIA })

  const alertsQuery = useApiQuery<ListAlertsResponse>(() => {
    const s = scopeRef.current
    const key = scopeKeyOf(s)
    const mediaTicket = beginMediaPrime()

    // The log is the one caller that asks for photo links: its rows show the photos.
    const query = {
      status: 'all',
      include: 'mediaUrls',
      ...(s.kind === 'robot' ? { robotId: s.robotId } : {}),
    } as const

    return robotsApi.listAlerts(query).then(res => {
      // The links are good whatever scope is current now, and must be cached before the rows
      // render, so each photo finds its link instead of asking a resolver for it.
      primeMediaUrls(res.data.mediaUrls, mediaTicket)
      // The cast is the same one the 5s poll makes: the listed row is a superset of the store item.
      if (key === scopeKeyOf(scopeRef.current)) setAlertsStamp({ key, rows: res.data.alerts as RobotAlertItem[] })

      return res
    })
  })

  const mediaQuery = useApiQuery<{ media: RobotMediaItem[] }>(
    () => {
      const key = scopeKeyOf(scopeRef.current)

      return robotsApi.listMedia(key).then(res => {
        if (key === scopeKeyOf(scopeRef.current)) setMediaStamp({ key, rows: res.data.media })

        return res
      })
    },
    { enabled: robotId !== undefined },
  )
  const refetchAlerts = alertsQuery.refetch
  const refetchMedia = mediaQuery.refetch

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchAlerts(), scopeRef.current.kind === 'robot' ? refetchMedia() : Promise.resolve()])
  }, [refetchAlerts, refetchMedia])

  // Scope change → refetch. The mount fetch is useApiQuery's own; a fleet→robot flip is its
  // `enabled` effect for media, so only a robot→robot switch needs media refetched here.
  const prevKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const prev = prevKeyRef.current

    prevKeyRef.current = scopeKey
    if (prev === null) return
    void refetchAlerts()
    if (prev !== 'fleet' && scopeRef.current.kind === 'robot') void refetchMedia()
  }, [scopeKey, refetchAlerts, refetchMedia])

  // The seen cache: overlay every store change into a per-mount map.
  const seenRef = useRef(new Map<string, RobotAlertItem>())
  const [seen, setSeen] = useState<RobotAlertItem[]>(NO_ALERTS)

  useEffect(() => {
    const m = seenRef.current

    for (const a of storeAlerts) m.set(a.alertId, overlayDefined(m.get(a.alertId), a))
    setSeen([...m.values()])
  }, [storeAlerts])

  // Media follow-up chain: bounded, restarted by the next trigger, never stacked.
  const chainRef = useRef<Array<ReturnType<typeof setTimeout>>>([])
  const startMediaFollowup = useCallback(() => {
    for (const t of chainRef.current) clearTimeout(t)
    chainRef.current =
      scopeRef.current.kind === 'robot' ? MEDIA_FOLLOWUP_MS.map(ms => setTimeout(() => void refetchMedia(), ms)) : []
  }, [refetchMedia])

  // A store CHANGE (not a store NOTIFY — the store hands out a fresh array every 5 s whether or not
  // anything moved) schedules one throttled refetch. The signature is what changed: an arrival, a
  // repeat (lastSeenAt bump), or a removal (acked/resolved/cleared anywhere, by anyone).
  const signature = useMemo(
    () =>
      storeAlerts
        .filter(a => scope.kind === 'fleet' || a.robotId === scope.robotId)
        .map(a => `${a.alertId}:${a.lastSeenAt ?? a.recordedAt ?? ''}:${a.sightingCount ?? 1}`)
        .sort()
        .join('|'),
    [storeAlerts, scope],
  )
  const latestMs = useMemo(
    () =>
      storeAlerts
        .filter(a => scope.kind === 'robot' && a.robotId === scope.robotId)
        .reduce((max, a) => Math.max(max, Date.parse(a.lastSeenAt ?? a.recordedAt ?? '') || 0), 0),
    [storeAlerts, scope],
  )
  const lastSignatureRef = useRef(signature)
  const lastLatestRef = useRef(latestMs)
  const lastRunRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (signature !== lastSignatureRef.current) {
      lastSignatureRef.current = signature
      if (timerRef.current === null) {
        const wait = Math.max(0, LOG_REFETCH_THROTTLE_MS - (Date.now() - lastRunRef.current))

        timerRef.current = setTimeout(() => {
          timerRef.current = null
          lastRunRef.current = Date.now()
          void refetchAll()
        }, wait)
      }
    }

    // A NEWER sighting for the selected helmet (not a removal): an alarm often brings a photo.
    if (latestMs > lastLatestRef.current) startMediaFollowup()
    lastLatestRef.current = latestMs
  }, [signature, latestMs, refetchAll, startMediaFollowup])

  // Dashboard-only signals: this element's refresh tick, and an operator asking for a photo.
  useEffect(() => {
    if (!bridge) return

    const offRefresh = elementId
      ? bridge.on('refresh-element', ({ elementId: targetId }) => {
          if (targetId === elementId) void refetchAll()
        })
      : () => {}

    const offRequested = bridge.on('robot:media-requested', ({ robotId: id }) => {
      if (scopeRef.current.kind === 'robot' && id === scopeRef.current.robotId) startMediaFollowup()
    })

    return () => {
      offRefresh()
      offRequested()
    }
  }, [bridge, elementId, refetchAll, startMediaFollowup])

  // The modest media interval: only while a helmet is selected on a dashboard.
  useEffect(() => {
    if (!bridge || robotId === undefined) return

    const id = setInterval(() => void refetchMedia(), MEDIA_REFETCH_MS)

    return () => clearInterval(id)
  }, [bridge, robotId, refetchMedia])

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
      for (const t of chainRef.current) clearTimeout(t)
    },
    [],
  )

  const fetched = alertsStamp.key === scopeKey ? alertsStamp.rows : NO_ALERTS
  const media = robotId !== undefined && mediaStamp.key === scopeKey ? mediaStamp.rows : NO_MEDIA

  return {
    fetched,
    media,
    seen,
    error: alertsQuery.error ?? mediaQuery.error,
    capped: { alerts: fetched.length >= SERVER_LIST_CAP, pictures: media.length >= SERVER_LIST_CAP },
    refetch: refetchAll,
  }
}
